/**
 * REMEMBERED_ORGANIZATION_STORAGE_KEY is the localStorage key under which the
 * last-visited organization id is remembered, so a returning user lands back in
 * the organization they were operating in. Namespaced under `mountaya.iam.` so it
 * never collides with other persisted client state.
 */
const REMEMBERED_ORGANIZATION_STORAGE_KEY = "mountaya.iam.organization_id";

/**
 * readRememberedOrganizationId returns the last-remembered organization id, or
 * null when none is stored, localStorage is unavailable (SSR / a build step
 * importing this module), or the read throws (Safari private mode raises on
 * access). The caller still validates the id against the live memberships, so a
 * stale id here is harmless.
 */
export const readRememberedOrganizationId = (): string | null => {
  try {
    return globalThis.window?.localStorage.getItem(REMEMBERED_ORGANIZATION_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

/**
 * writeRememberedOrganizationId persists the active organization id so the next
 * visit resolves it first. It is a best-effort write: a missing window or a
 * storage failure (private mode, quota) is swallowed, because remembering the
 * organization is a convenience, never a correctness requirement.
 */
export const writeRememberedOrganizationId = (id: string): void => {
  try {
    globalThis.window?.localStorage.setItem(REMEMBERED_ORGANIZATION_STORAGE_KEY, id);
  } catch {
    // Storage unavailable (private mode / quota): the next visit simply falls
    // back to the first active organization. No user-facing failure.
  }
};
